export const toHex = ([r,g,b]) => "#"+[r,g,b].map(v=>v.toString(16).padStart(2,"0")).join("");
export const toRgbStr = ([r,g,b]) => `rgb(${r}, ${g}, ${b})`;

export function toHsl([r,g,b]) {
  r/=255; g/=255; b/=255;
  const max=Math.max(r,g,b), min=Math.min(r,g,b);
  let h,s,l=(max+min)/2;
  if(max===min){h=s=0;}
  else{
    const d=max-min;
    s=l>.5?d/(2-max-min):d/(max+min);
    switch(max){
      case r:h=((g-b)/d+(g<b?6:0))/6;break;
      case g:h=((b-r)/d+2)/6;break;
      default:h=((r-g)/d+4)/6;
    }
  }
  return [Math.round(h*360),Math.round(s*100),Math.round(l*100)];
}

export const toHslStr = ([r,g,b])=>{const[h,s,l]=toHsl([r,g,b]);return`hsl(${h}, ${s}%, ${l}%)`;};

export function luminance([r,g,b]){
  return [r,g,b].reduce((s,c,i)=>{
    c/=255; c=c<=.03928?c/12.92:Math.pow((c+.055)/1.055,2.4);
    return s+c*[.2126,.7152,.0722][i];
  },0);
}

export function contrast(c1,c2){
  const l1=luminance(c1),l2=luminance(c2);
  return (Math.max(l1,l2)+.05)/(Math.min(l1,l2)+.05);
}

export function warmth(palette){
  const s=palette.reduce((a,[r,,b])=>a+(r-b),0);
  return Math.round(Math.max(0,Math.min(100,50+(s/(palette.length*255))*200)));
}

export function fmtColor(rgb,fmt){
  if(fmt==="hex") return toHex(rgb);
  if(fmt==="rgb") return toRgbStr(rgb);
  return toHslStr(rgb);
}

export const TEXT_CANDIDATES = [
  {rgb:[255,255,255],name:"Pure white",    note:"Universal - always safe"},
  {rgb:[232,255,71], name:"Electric lime", note:"Punchy - game-native energy"},
  {rgb:[245,230,200],name:"Warm cream",    note:"Premium, cinematic"},
  {rgb:[180,230,255],name:"Ice blue",      note:"Cool, tech - dark KVs"},
  {rgb:[255,220,100],name:"Gold",           note:"Heroic - classic game text"},
  {rgb:[200,255,220],name:"Mint",           note:"Fresh - works on deep darks"},
  {rgb:[0,0,0],      name:"Pure black",    note:"Light backgrounds only"},
  {rgb:[255,100,80], name:"Hot coral",     note:"Usually clashes on busy KVs"},
];

export function getTextRecs(palette){
  if(!palette.length) return [];
  const bg=palette.slice(0,3);

  // Score fixed candidates
  const fixed=TEXT_CANDIDATES.map(c=>{
    const avg=bg.map(b=>contrast(c.rgb,b)).reduce((s,v)=>s+v,0)/bg.length;
    const status=avg>=4.5?"great":avg>=3?"ok":"bad";
    const statusNote=avg>=4.5?"Body text OK":avg>=3?"Titles only":"Avoid";
    return{...c,avg:avg.toFixed(1),status,statusNote};
  });

  // Also derive text colours from the actual palette (lightened versions)
  const derived=palette.slice(0,4).map((rgb,i)=>{
    const lightened=rgb.map(c=>Math.min(255,Math.round(c*0.25+185)));
    const avg=bg.map(b=>contrast(lightened,b)).reduce((s,v)=>s+v,0)/bg.length;
    if(avg<3) return null; // skip low contrast
    const status=avg>=4.5?"great":avg>=3?"ok":"bad";
    const statusNote=avg>=4.5?"Body text OK":avg>=3?"Titles only":"Avoid";
    const [,s,l]=toHsl(rgb);
    return{rgb:lightened,name:`Image tone ${i+1}`,note:`Derived from palette  -  ${s>50?"vivid":"muted"}, works with this image's colour temperature`,avg:avg.toFixed(1),status,statusNote,fromPalette:true};
  }).filter(Boolean);

  return [...fixed,...derived].sort((a,b)=>parseFloat(b.avg)-parseFloat(a.avg)).slice(0,7);
}

export function getGlow(palette){
  if(!palette.length) return null;
  const[r,g,b]=palette[0];
  return{
    primary:[Math.round(r*.28),Math.round(g*.28),Math.round(b*.28)],
    alt:[Math.round((255-r)*.35),Math.round((255-g)*.35),Math.round((255-b)*.35)],
  };
}

function loadImageDirect(url, cors=true){
  return new Promise((resolve,reject)=>{
    const img=new Image();
    if(cors && !url.startsWith("data:")) img.crossOrigin="Anonymous";
    img.onload=()=>resolve(img);
    img.onerror=()=>reject(new Error("load failed"));
    img.src=url;
  });
}

function loadImage(url){
  // 1. Try direct load (works for base64 and CORS-friendly CDNs)
  return loadImageDirect(url).catch(()=>{
    // 2. For external URLs: retry via NAS proxy (adds CORS headers server-side)
    if(url.startsWith("data:") || url.startsWith("blob:")) throw new Error("Cannot proxy data URI");
    const proxyUrl="/api/proxy?url="+encodeURIComponent(url);
    return loadImageDirect(proxyUrl, false); // proxy adds CORS, no crossOrigin needed
  });
}

export async function extractPalette(url){
  const img=await loadImage(url);
  const canvas=document.createElement("canvas");
  const SIZE=200; canvas.width=SIZE; canvas.height=SIZE;
  const ctx=canvas.getContext("2d");
  ctx.drawImage(img,0,0,SIZE,SIZE);
  const data=ctx.getImageData(0,0,SIZE,SIZE).data;
  const pixels=[];
  for(let i=0;i<data.length;i+=4){
    if(data[i+3]<128) continue;
    pixels.push([data[i],data[i+1],data[i+2]]);
  }
  const colors=kMeansColors(pixels,6);
  return{colors,warm:warmth(colors)};
}

function kMeansColors(pixels,k){
  if(!pixels.length) return Array(k).fill([128,128,128]);
  const sample=pixels.length>2000?pixels.filter((_,i)=>i%Math.floor(pixels.length/2000)===0):pixels;
  let centroids=[];
  for(let i=0;i<k;i++) centroids.push(sample[Math.floor((i/k)*sample.length)]);
  for(let iter=0;iter<8;iter++){
    const clusters=Array.from({length:k},()=>[]);
    for(const px of sample){
      let best=0,bestDist=Infinity;
      for(let c=0;c<k;c++){
        const d=(px[0]-centroids[c][0])**2+(px[1]-centroids[c][1])**2+(px[2]-centroids[c][2])**2;
        if(d<bestDist){bestDist=d;best=c;}
      }
      clusters[best].push(px);
    }
    centroids=clusters.map((cl,i)=>{
      if(!cl.length) return centroids[i];
      return[
        Math.round(cl.reduce((s,p)=>s+p[0],0)/cl.length),
        Math.round(cl.reduce((s,p)=>s+p[1],0)/cl.length),
        Math.round(cl.reduce((s,p)=>s+p[2],0)/cl.length),
      ];
    });
  }
  return centroids.sort((a,b)=>(a[0]*.299+a[1]*.587+a[2]*.114)-(b[0]*.299+b[1]*.587+b[2]*.114));
}
